/* eslint-disable max-lines */
import { StatusCodes } from 'http-status-codes';

import { formatMessageDate } from 'utils/date/formatUnixDate';

import firebaseNode from '../../../../firebase/firebaseNode';
import { FirebaseMain } from '../../../../firebase/firebase';

import { NAME } from './selectors';
import { ACTION_NAMES as ACTIONS } from './actionTypes';
import {
  deleteConv,
  fetchContacts,
  fetchConversation,
  fetchPreviews,
  markAsRead,
  postFile,
  postMessage,
  postReportAbuse,
  putConversation,
  postBanUser,
  postDeleteMessage,
  postMarkAsSpam,
} from './Api';
import {
  createContactGroups,
  getActiveConversationPreloadDetails,
  getMediaMessage,
  getMessagePostParams,
  getPreviewFromConversation,
  isSameConversationOpen,
  validateFile,
} from './utils';


async function _loadConversation(dispatch, getState, threadId) {
  const previews = getState()[NAME].get('previews').toJS();
  const preloadDetails = getActiveConversationPreloadDetails(previews, threadId);

  dispatch({
    type: ACTIONS.SET_CONVERSATION_LOADING,
    payload: { activeConversationPreloadDetails: preloadDetails },
  });

  const { result, error } = await fetchConversation(threadId);

  if (!result || error) {
    dispatch({ type: ACTIONS.SET_CONVERSATION_LOADING_ERROR });

    return null;
  }

  dispatch({
    type: ACTIONS.SET_CONVERSATION_LOADING_SUCCESS,
    payload: { activeConversation: result },
  });

  return result;
}

async function _markAsRead(dispatch, getState, threadId) {
  const { result, error } = await markAsRead(threadId);

  if (!result || error) {
    // no need for error,
    // as there is nothing that makes sense to be done within the user flow
    return;
  }

  const previews = getState()[NAME].get('previews').toJS();
  const updatedPreviews = previews.map((preview) => {
    if (preview.threadId === threadId) {
      return { ...preview, unreadMsgCount: 0 };
    }

    return preview;
  });

  dispatch({
    type: ACTIONS.UPDATE_PREVIEWS,
    payload: { previews: updatedPreviews },
  });
}

function setInitialLoadExecuted() {
  return (dispatch) => dispatch({ type: ACTIONS.INITIAL_LOAD_EXECUTED });
}

function setActiveTab(activeTab) {
  return (dispatch) => dispatch({
    type: ACTIONS.SET_ACTIVE_TAB,
    payload: { activeTab },
  });
}

function initializeConversationsTab() {
  return async(dispatch, getState) => {
    dispatch({ type: ACTIONS.INITIALIZE_CONVERSATIONS_TAB });

    const { result, error } = await fetchPreviews();

    if (!result || error) {
      dispatch({ type: ACTIONS.SET_PREVIEWS_LOADING_ERROR });
    }

    dispatch({
      type: ACTIONS.SET_PREVIEWS_LOADING_SUCCESS,
      payload: { previews: result },
    });

    const firstConversationId = result?.[0]?.threadId;
    if (!firstConversationId) {
      return;
    }

    await _loadConversation(dispatch, getState, firstConversationId);
    await _markAsRead(dispatch, getState, firstConversationId);
  };
}

function loadPreviews(filter) {
  return async(dispatch) => {
    dispatch({ type: ACTIONS.SET_PREVIEWS_LOADING });

    const { result, error } = await fetchPreviews(filter);

    if (!result || error) {
      dispatch({ type: ACTIONS.SET_PREVIEWS_LOADING_ERROR });
    }

    dispatch({
      type: ACTIONS.SET_PREVIEWS_LOADING_SUCCESS,
      payload: { previews: result },
    });
  };
}

function loadContacts(search) {
  return async(dispatch) => {
    dispatch({ type: ACTIONS.SET_CONTACTS_LOADING });

    const { result, error } = await fetchContacts(search);

    if (!result || error) {
      dispatch({ type: ACTIONS.SET_CONTACTS_LOADING_ERROR });
    }

    dispatch({
      type: ACTIONS.SET_CONTACTS_LOADING_SUCCESS,
      payload: {
        contacts: result,
        groupedContacts: createContactGroups(result),
      },
    });
  };
}

function loadConversation(threadId) {
  return async(dispatch, getState) => {
    const result = await _loadConversation(dispatch, getState, threadId);

    if (!result || !isSameConversationOpen(getState, threadId)) {
      return;
    }

    await _markAsRead(dispatch, getState, threadId);
  };
}

function createConversation(receiverName) {
  return async(dispatch, getState) => {
    dispatch({ type: ACTIONS.CREATE_CONVERSATION_LOADING });
    const {
      result: putConversationResult,
      error: putConversationError,
    } = await putConversation(receiverName);

    if (!putConversationResult || putConversationError) {
      dispatch({ type: ACTIONS.CREATE_CONVERSATION_LOADING_ERROR });
      // must add error state, and dispatch properly

      return;
    }

    const newThreadId = putConversationResult;
    const {
      result: fetchConversationResult,
      error: fetchConversationError,
    } = await fetchConversation(newThreadId);

    if (!fetchConversationResult || fetchConversationError) {
      dispatch({ type: ACTIONS.CREATE_CONVERSATION_LOADING_ERROR });
      // must add error state, and dispatch properly

      return;
    }

    const newConversation = fetchConversationResult;
    const newPreview = {
      isMassAdminThread: newConversation.isMassAdminThread,
      lastMessageReadId: newConversation.lastReadMsgId,
      lastPerformerMassMessageReadId: 0,
      preview: {},
      read: false,
      receiverName,
      threadId: newThreadId,
      unreadMsgCount: 0,
      users: newConversation.users,
    };
    const previews = getState()[NAME].get('previews').toJS();
    previews.unshift(newPreview);

    dispatch({
      type: ACTIONS.CREATE_CONVERSATION_LOADING_SUCCESS,
      payload: {
        activeConversation: newConversation,
        previews,
      },
    });
  };
}

function resetCreateConversationStatus() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.RESET_CREATE_CONVERSATION_STATUS });
  };
}


function deleteConversation(threadId) {
  return async(dispatch, getState) => {

    dispatch({ type: ACTIONS.DELETE_CONVERSATION_LOADING });

    const { result, error } = await deleteConv(threadId);

    if (error || result.status !== 'success') {
      dispatch({ type: ACTIONS.DELETE_CONVERSATION_LOADING_ERROR });

      return;
    }

    const previews = getState()[NAME].get('previews').toJS();
    const updatedPreviews = previews.filter((item) => item.threadId !== threadId);

    dispatch({
      type: ACTIONS.DELETE_CONVERSATION_LOADING_SUCCESS,
      payload: { previews: updatedPreviews },
    });

    const index = previews.findIndex((p) => p.threadId === threadId);
    const nextConversation = (updatedPreviews[index] || updatedPreviews[index - 1]);
    const newConversationThreadId = nextConversation?.threadId;
    if (!newConversationThreadId) {
      dispatch({ type: ACTIONS.UNSET_CONVERSATION });

      return;
    }

    await _loadConversation(dispatch, getState, newConversationThreadId);
  };
}

function resetDeleteConversationStatus() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.RESET_DELETE_CONVERSATION_STATUS });
  };
}


function resetActiveConversation() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.UNSET_CONVERSATION });
  };
}

function loadMoreMessages({ threadId, offset }) {
  return async(dispatch, getState) => {
    dispatch({ type: ACTIONS.SET_CONVERSATION_LOADING_MORE });

    const { result, error } = await fetchConversation(threadId, offset);

    if (!isSameConversationOpen(getState, threadId)) {
      return;
    }

    if (!result || error) {
      dispatch({ type: ACTIONS.SET_CONVERSATION_LOADING_MORE_ERROR });

      return;
    }

    const activeConversation = getState()[NAME].get('activeConversation').toJS();
    activeConversation.canLoadMore = result.canLoadMore;
    activeConversation.messages = [
      ...activeConversation.messages,
      ...result.messages,
    ];

    dispatch({
      type: ACTIONS.SET_CONVERSATION_LOADING_MORE_SUCCESS,
      payload: { activeConversation },
    });
  };
}

function sendMessage({ message, threadId }) {
  return async() => {
    const params = getMessagePostParams({ message, threadId });

    const { result, error } = await postMessage(params);

    if (!result || error) {
      // must add error state, and dispatch properly
      // this requires an entirely new improvement flow
    }
  };
}

function reportAbuse(data) {
  return async(dispatch) => {
    dispatch({ type: ACTIONS.SET_REPORT_ABUSE_LOADING });

    // this returns 200 even when "error"
    const { result, error } = await postReportAbuse(data);
    const errorMessages = [
      'You have to wait 60 seconds between 2 abuse reports',
      'Sorry, you may only report someone once per day. Your previous report has been received and will be processed.',
    ];

    if (!result || error || errorMessages.includes(result)) {
      const reportAbuseErrorMessage = result || '';

      dispatch({
        type: ACTIONS.SET_REPORT_ABUSE_LOADING_ERROR,
        payload: { reportAbuseErrorMessage },
      });

      return;
    }

    dispatch({ type: ACTIONS.SET_REPORT_ABUSE_LOADING_SUCCESS });
  };
}

function resetReportAbuseStatus() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.RESET_REPORT_ABUSE_STATUS });
  };
}

function banUser(receiverName) {
  return async(dispatch, getState) => {
    dispatch({ type: ACTIONS.SET_BAN_USER_LOADING });

    const { error } = await postBanUser(receiverName);

    if (error) {
      dispatch({ type: ACTIONS.SET_BAN_USER_LOADING_ERROR });

      return;
    }

    const threadId = getState()[NAME].getIn(['activeConversation', 'threadId']);
    const previews = getState()[NAME].get('previews').toJS();
    const updatedPreviews = previews.filter((item) => item.threadId !== threadId);

    dispatch({
      type: ACTIONS.SET_BAN_USER_LOADING_SUCCESS,
      payload: { previews: updatedPreviews },
    });

    const index = previews.findIndex((p) => p.threadId === threadId);
    const nextConversation = (updatedPreviews[index] || updatedPreviews[index - 1]);
    const newConversationThreadId = nextConversation?.threadId;
    if (!newConversationThreadId) {
      dispatch({ type: ACTIONS.UNSET_CONVERSATION });

      return;
    }

    await _loadConversation(dispatch, getState, newConversationThreadId);
  };
}

function resetBanUserStatus() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.RESET_BAN_USER_STATUS });
  };
}

function markAsSpam({ threadId, messageId }) {
  return async(dispatch) => {
    dispatch({ type: ACTIONS.MARK_AS_SPAM_LOADING });

    const { error } = await postMarkAsSpam({ threadId, messageId });

    if (error) {
      dispatch({ type: ACTIONS.MARK_AS_SPAM_LOADING_ERROR });

      return;
    }

    dispatch({ type: ACTIONS.MARK_AS_SPAM_LOADING_SUCCESS });
  };
}

function resetMarkAsSpamState() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.RESET_MARK_AS_SPAM_STATUS });
  };
}

function deleteMessage({ threadId, messageId }) {
  return async(dispatch, getState) => {
    dispatch({ type: ACTIONS.DELETE_MESSAGE_LOADING });

    const { error } = await postDeleteMessage({ threadId, messageId });

    if (error) {
      dispatch({ type: ACTIONS.DELETE_MESSAGE_LOADING_ERROR });

      return;
    }

    const activeConversation = getState()[NAME].get('activeConversation').toJS();
    activeConversation.messages = activeConversation.messages.filter(
      (message) => message.msgId !== messageId
    );

    dispatch({
      type: ACTIONS.DELETE_MESSAGE_LOADING_SUCCESS,
      payload: { activeConversation },
    });
  };
}

function resetDeleteMessageState() {
  return (dispatch) => {
    dispatch({ type: ACTIONS.RESET_DELETE_MESSAGE_STATUS });
  };
}


function loadGroupConversation(threadId) {
  return async(dispatch) => {

    dispatch({ type: ACTIONS.SET_GROUP_CONVERSATION_LOADING });

    const { result, error } = await fetchConversation(threadId);

    if (!result || error) {
      dispatch({ type: ACTIONS.SET_GROUP_CONVERSATION_LOADING_ERROR });

      return;
    }

    dispatch({
      type: ACTIONS.SET_GROUP_CONVERSATION_LOADING_SUCCESS,
      payload: { groupConversation: result },
    });
  };
}

function loadMoreGroupMessages({ threadId, offset }) {
  return async(dispatch, getState) => {
    dispatch({ type: ACTIONS.SET_GROUP_CONVERSATION_LOADING_MORE });

    const { result, error } = await fetchConversation(threadId, offset);

    const activeTab = getState()[NAME].get('activeTab');
    if (activeTab !== 'group') {
      return;
    }

    if (!result || error) {
      dispatch({ type: ACTIONS.SET_GROUP_CONVERSATION_LOADING_MORE_ERROR });

      return;
    }

    const groupConversation = getState()[NAME].get('groupConversation').toJS();
    groupConversation.canLoadMore = result.canLoadMore;
    groupConversation.messages = [
      ...groupConversation.messages,
      ...result.messages,
    ];

    dispatch({
      type: ACTIONS.SET_GROUP_CONVERSATION_LOADING_MORE_SUCCESS,
      payload: { groupConversation },
    });
  };
}

function createFileContext(dispatch, getState, { file, threadId }) {
  const fileValidation = validateFile(file);

  const { format, previewSrc } = fileValidation;
  const msgId = Date.now();
  const mType = format;

  let accessUrl = undefined;
  let tmpUploadUrl = undefined;

  const _addFileUploadProgress = (params) => {
    const uploads = getState()[NAME].get('uploads').toJS();
    if (!uploads[threadId]) {
      uploads[threadId] = { [msgId]: params };
    } else {
      uploads[threadId][msgId] = params;
    }

    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_PROGRESS,
      payload: { uploads },
    });
  };

  const _updateFileUploadProgress = (progress) => {
    const uploads = getState()[NAME].get('uploads').toJS();
    const updated = { ...uploads[threadId][msgId], progress };
    uploads[threadId][msgId] = updated;

    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_PROGRESS,
      payload: { uploads },
    });
  };

  const _removeFileUploadProgress = () => {
    const uploads = getState()[NAME].get('uploads').toJS();
    delete uploads[threadId]?.[msgId];
    if (!Object.keys(uploads[threadId]).length) {
      delete uploads[threadId];
    }

    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_PROGRESS,
      payload: { uploads },
    });
  };

  const _setFileError = (errors) => {
    const uploads = getState()[NAME].get('uploads').toJS();
    const updated = {
      ...uploads[threadId][msgId],
      progress: 0,
      ...errors,
    };
    uploads[threadId][msgId] = updated;

    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_PROGRESS,
      payload: { uploads },
    });
  };

  const _sendMessage = async() => {
    const formattedMessage = getMediaMessage({ mType, accessUrl, threadId });
    const {
      result: postMessageResult,
      error: postMessageError,
    } = await postMessage(formattedMessage);

    if (!postMessageResult || postMessageError) {
      _setFileError({
        uploadError: false,
        sendError: true,
      });

      return;
    }

    _removeFileUploadProgress();
  };

  const _uploadFile = async() => {
    const { result, error } = await postFile(file);

    if (!result || error) {
      // must add proper error checks

      return;
    }

    const {
      accessUrl: responseAccessUrl,
      tmpUploadUrl: responseTmpUploadUrl,
    } = result;

    accessUrl = responseAccessUrl;
    tmpUploadUrl = responseTmpUploadUrl;

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', tmpUploadUrl, true);

    xhr.upload.onprogress = function({ loaded, total }) {
      const progress = parseInt(Math.round((loaded * 100) / total), 10);
      _updateFileUploadProgress(progress);
    };

    xhr.onabort = function() {
      _removeFileUploadProgress();
    };

    xhr.onload = function() {
      if (xhr.readyState === 4 && xhr.status === StatusCodes.CREATED) {
        _sendMessage();
      }
    };

    xhr.onerror = function() {
      _setFileError({
        uploadError: true,
        sendError: false,
      });
    };

    _addFileUploadProgress({
      mType: format,
      previewSrc,
      progress: 0,
      sendError: false,
      uploadError: false,
      onAbort: () => xhr.abort(),
      onRetryUpload: _uploadFile,
      onRetrySend: _sendMessage,
    });

    xhr.send(file);
  };

  _uploadFile();
}

function uploadFile({ file, threadId }) {
  return (dispatch, getState) => {
    createFileContext(dispatch, getState, { file, threadId });
  };
}

function watchConversationState(user) {
  const { username } = user;

  const conversationStateRef =
    FirebaseMain
      .child(firebaseNode.USER_NOTIFICATION)
      .child(username.toLowerCase());

  return (dispatch, getState) => {
    conversationStateRef.on('value', async(snapshot) => {
      const message = snapshot.val();

      const {
        msgId,
        sendDate,
        senderId,
        threadId,
      } = message || {};

      if (!message || !msgId || !senderId) {
        return;
      }

      message.sendDate = formatMessageDate(sendDate);

      const userId = user.user.getIn(['user', 'userId']);
      const isGroupConversation = threadId === -1 * userId;
      if (isGroupConversation) {
        const groupConversation = getState()[NAME].get('groupConversation').toJS();
        groupConversation.messages.unshift(message);

        dispatch({
          type: ACTIONS.ADD_GROUP_MESSAGE,
          payload: { groupConversation },
        });

        return;
      }

      const previews = getState()[NAME].get('previews').toJS();
      const activeConversation = getState()[NAME].get('activeConversation').toJS();
      const activeConversationThreadId = activeConversation.threadId;

      const isActiveConversation = (threadId === activeConversationThreadId);
      if (isActiveConversation) {
        const updatedPreviews = [];
        previews.forEach((item) => {
          if (item.threadId === threadId) {
            item.preview = { ...item.preview, ...message };
            updatedPreviews.unshift(item);
          } else {
            updatedPreviews.push(item);
          }
        });

        activeConversation.messages.unshift(message);

        dispatch({
          type: ACTIONS.ADD_MESSAGE,
          payload: {
            previews: updatedPreviews,
            activeConversation,
          },
        });

        _markAsRead(dispatch, getState, activeConversationThreadId);

        return;
      }

      const isExistingPreview = previews.find(
        (preview) => preview.threadId === threadId
      );

      if (isExistingPreview) {
        const isSentByUser = userId === senderId;

        const updatedPreviews = [];
        previews.forEach((item) => {
          if (item.threadId === threadId) {
            item.preview = { ...item.preview, ...message };
            if (!isSentByUser) {
              item.unreadMsgCount += 1;
            }
            updatedPreviews.unshift(item);
          } else {
            updatedPreviews.push(item);
          }
        });

        dispatch({
          type: ACTIONS.UPDATE_PREVIEWS,
          payload: { previews: updatedPreviews },
        });

        return;
      }

      const { result } = await fetchConversation(threadId);

      const preview = getPreviewFromConversation(message, result);
      const updatedPreviews = [preview, ...previews];

      dispatch({
        type: ACTIONS.UPDATE_PREVIEWS,
        payload: { previews: updatedPreviews },
      });
    });
  };
}

const actionCreators = {
  initializeConversationsTab,
  setInitialLoadExecuted,
  setActiveTab,

  loadPreviews,
  loadContacts,
  loadConversation,

  createConversation,
  resetCreateConversationStatus,

  loadMoreMessages,

  deleteConversation,
  resetDeleteConversationStatus,

  watchConversationState,
  resetActiveConversation,

  reportAbuse,
  resetReportAbuseStatus,

  markAsSpam,
  resetMarkAsSpamState,

  deleteMessage,
  resetDeleteMessageState,

  banUser,
  resetBanUserStatus,

  sendMessage,

  uploadFile,

  loadGroupConversation,
  loadMoreGroupMessages,
};

export default actionCreators;
